/**
 * useUserMenu Hook
 * Custom hook for UserMenu component logic with memoization
 * Following best practices with useMemo for performance optimization
 * Refactored to be universal - accepts items array instead of individual callbacks
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserMenuItem } from './UserMenu.types';

interface User {
  username?: string;
  email?: string;
  imageURL?: string;
  avatar?: string;
}

interface UseUserMenuProps {
  user?: User;
  items?: UserMenuItem[];
}

export const useUserMenu = ({ user, items }: UseUserMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Memoize display name
  const displayName = useMemo(
    () => user?.username || user?.email || t('common.guest'),
    [user?.username, user?.email, t]
  );

  // Memoize user image
  const userImage = useMemo(
    () => user?.imageURL || user?.avatar,
    [user?.imageURL, user?.avatar]
  );

  // Memoize avatar initial
  const avatarInitial = useMemo(
    () => displayName.charAt(0).toUpperCase(),
    [displayName]
  );

  // Memoize menu items (no filtering needed, items come pre-configured)
  const menuItems = useMemo(() => items || [], [items]);

  // Memoized toggle function
  const toggleMenu = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Memoized menu item click handler
  const handleMenuItemClick = useCallback(
    (item: UserMenuItem) => {
      setIsOpen(false);
      
      // Handle navigation if path is provided
      if (item.path) {
        navigate(item.path);
      }
      
      // Handle onClick callback if provided
      if (item.onClick) {
        item.onClick();
      }
    },
    [navigate]
  );

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return {
    isOpen,
    menuRef,
    displayName,
    userImage,
    avatarInitial,
    menuItems,
    toggleMenu,
    handleMenuItemClick,
  };
};
